/**
 * Module: FindsScreen
 * Role: Overlay-page at /finds — 2-col grid of buyer's approved slots.
 *       Backend expands `status=approved` to all 4 domain statuses server-side
 *       (src/api/v2/buyer/_slots_db.py:148 _api_to_domain_statuses).
 *
 * Visual: matches prototype.jsx FindsScreen (screens.jsx:824-844) — 2-col grid,
 * Card p=0 with overflow:hidden, product image h=150, marketplace badge overlay,
 * 2-line title clamp, Caveat "+X ₽" cashback annotation.
 *
 * Backend contract:
 *   - GET /api/v2/buyer/slots?status=approved&limit=20 → SlotListResponse
 *   - GET /api/v2/buyer/tasks/{offer_id}              → TaskItem (for product
 *     enrichment, since SlotListItem has no product_title / image / marketplace)
 *
 * Uses:
 *   - atoms (IOSScreen, PageHeader, Card, ProductImg, Skeleton, EmptyState)
 *   - @tanstack/react-query (useQuery, useQueries)
 *   - api/client (apiFetch)
 *   - hooks/usePlatformBack (TG BackButton)
 *
 * Used by:
 *   - App.tsx (route /finds, sibling of AppShell)
 */
import { CSSProperties, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import {
  IOSScreen, PageHeader, Card, ProductImg, Skeleton, EmptyState,
} from '../components/atoms';
import { apiFetch } from '../api/client';
import { usePlatformBack } from '../hooks/usePlatformBack';
import { P, S, T } from '../styles/tokens';

// ─── Backend contracts (L-240 inline) ────────────────────────────────────────
interface SlotListItem {
  id: string;
  offer_id: string;
  campaign_id: string;
  status: string;
  current_step: string;
  reward_kopecks: number;
  deadline_at: string | null;
  created_at: string;
}
interface SlotListResponse {
  items: SlotListItem[];
  meta: { total: number; limit: number; offset: number; has_next: boolean };
}
interface TaskItem {
  offer_id: string;
  campaign_id: string;
  product_title: string;
  product_image_url: string | null;
  cashback_kopecks: number;
  marketplace: string;
  slots_available: number;
  schedule_date: string;
}

// ─── FindsCard ────────────────────────────────────────────────────────────────

interface FindsCardProps {
  slot: SlotListItem;
  task: TaskItem | undefined;
  onTap: () => void;
}

function FindsCard({ slot, task, onTap }: FindsCardProps) {
  const hue = (task?.product_title?.charCodeAt(0) ?? slot.offer_id.charCodeAt(0)) % 360;
  const rewardRubles = Math.round(slot.reward_kopecks / 100);

  return (
    <Card
      p={0}
      r={16}
      hover
      onClick={onTap}
      style={{ overflow: 'hidden', cursor: 'pointer' }}
    >
      {/* ── Image ── */}
      <div style={{ position: 'relative' }}>
        {task?.product_image_url ? (
          <img
            src={task.product_image_url}
            alt={task.product_title}
            style={{
              width: '100%',
              height: 150,
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <ProductImg h={150} w="100%" r={0} hue={hue} />
        )}

        {/* Marketplace badge — top-left white pill */}
        {task?.marketplace && (
          <span
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              background: 'rgba(255,255,255,0.92)',
              color: S.label,
              padding: '3px 8px',
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0.1,
              lineHeight: 1.4,
            }}
          >
            {task.marketplace}
          </span>
        )}
      </div>

      {/* ── Card body ── */}
      <div style={{ padding: '8px 10px 10px' }}>
        {/* Title — 2-line clamp, subheadline 600 per prototype vitrina grid */}
        <div
          style={{
            font: T.subhead,
            fontWeight: 600,
            color: S.label,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 36,
          } as CSSProperties}
        >
          {task?.product_title ?? '—'}
        </div>

        {/* Marketplace label + Caveat reward row (prototype screens.jsx:835-838) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 6,
            marginTop: 6,
          }}
        >
          <span
            style={{
              font: T.captionAlt,
              color: S.secondaryLabel,
            }}
          >
            {task?.marketplace ?? ''}
          </span>
          <span
            style={{
              fontFamily: T.fontAccent,
              fontSize: 20,
              color: P.terraDark,
              fontWeight: 700,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            +{rewardRubles}&nbsp;₽
          </span>
        </div>
      </div>
    </Card>
  );
}

// ─── FindsScreen ─────────────────────────────────────────────────────────────
export default function FindsScreen() {
  const navigate = useNavigate();
  const onBack = () => navigate(-1);
  usePlatformBack();

  // Step 1: single slot query — backend expands 'approved' to all 4 domain statuses
  const slotsQuery = useQuery({
    queryKey: ['buyer', 'slots', 'approved'],
    queryFn: () => apiFetch<SlotListResponse>('/api/v2/buyer/slots?status=approved&limit=20'),
    staleTime: 30_000,
    retry: false,
  });

  const slots: SlotListItem[] = useMemo(
    () => slotsQuery.data?.items ?? [],
    [slotsQuery.data],
  );

  // Step 2: enrich each slot with product details via /tasks/{offer_id}
  // (slot row has no product_title/image — see Drift Log entry 4)
  const taskQueries = useQueries({
    queries: slots.map((s) => ({
      queryKey: ['buyer', 'task', s.offer_id],
      queryFn: () => apiFetch<TaskItem>(`/api/v2/buyer/tasks/${s.offer_id}`),
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 min — task detail rarely changes for finished slots
    })),
  });

  const tasksByOfferId: Record<string, TaskItem | undefined> = useMemo(() => {
    const m: Record<string, TaskItem | undefined> = {};
    for (const [i, q] of taskQueries.entries()) {
      const s = slots[i];
      if (s) m[s.offer_id] = q.data;
    }
    return m;
  }, [taskQueries, slots]);

  return (
    <IOSScreen tabBarSpace={false}>
      <PageHeader title="Мои находки" subtitle="Что ты уже забирала" onBack={onBack} />

      {slotsQuery.isLoading && <FindsSkeleton />}

      {slotsQuery.isError && (
        <div style={{ padding: '0 16px' }}>
          <EmptyState title="Не удалось загрузить" subtitle="Попробуй позже" />
        </div>
      )}

      {slots.length === 0 && !slotsQuery.isLoading && !slotsQuery.isError && (
        <div style={{ padding: '0 16px' }}>
          <EmptyState
            title="Пока ничего не забирала"
            subtitle="Зайди во вкладку «Задания»"
          />
        </div>
      )}

      {slots.length > 0 && !slotsQuery.isLoading && !slotsQuery.isError && (
        <div
          style={{
            padding: '14px 12px 0',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
          }}
        >
          {slots.map((s) => (
            <FindsCard
              key={s.id}
              slot={s}
              task={tasksByOfferId[s.offer_id]}
              onTap={() => navigate(`/product/${s.offer_id}`)}
            />
          ))}
        </div>
      )}
    </IOSScreen>
  );
}

// ─── Skeleton (6 cards, 2 cols × 3 rows) ────────────────────────────────────
function FindsSkeleton() {
  return (
    <div
      style={{
        padding: '14px 12px 0',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 10,
      }}
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i}>
          <Skeleton h={150} r={16} />
          <Skeleton h={14} w="80%" style={{ marginTop: 8 }} />
          <Skeleton h={12} w="55%" style={{ marginTop: 6 }} />
        </div>
      ))}
    </div>
  );
}
